import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';

import { environment } from 'src/environments/environment';
import { AuthenticationService } from './authentication.service';
import { getBrowserTimezone } from '../utils/timezone';

// ---------------------------------------------------------------- chat

/** A single turn of the conversation, as Atlas expects it in `history`. */
export interface AtlasTurn {
  role: 'user' | 'assistant';
  content: string;
}

/** One tool call Atlas made, and what it returned — the numbers behind an answer. */
export interface AtlasEvidence {
  tool: string;
  input?: Record<string, unknown>;
  result: unknown;
}

export interface AtlasChatResponse {
  answer: string;
  evidence?: AtlasEvidence[];
  /** Server-side trace id — quote it when reporting a bad answer. Null on a refusal. */
  auditId?: string | null;
}

export interface AtlasBriefingResponse {
  answer?: string;
  evidence?: AtlasEvidence[];
  auditId?: string | null;
}

// --------------------------------------------------------------- roles

export type AtlasRole = 'owner' | 'executive' | 'management' | 'customer_service';

export interface AtlasRoleAssignment {
  adminId: string;
  role: AtlasRole;
  scope: string;
  /** Plain-English summary written by the server. Print as-is; never compose it here. */
  sees: string;
  assignedBy?: string | null;
  updatedAt?: string | null;
  /** false → this person is at or above the viewer's level; lock the row. */
  editable: boolean;
}

/** One row of the "who can see what" legend. */
export interface AtlasRoleDomains {
  privatePnl: boolean;
  opFinance: boolean;
  fraudDC: boolean;
  fraudStaff: boolean;
  safety: boolean;
  ops: boolean;
  scoped: boolean;
}

export interface AtlasRolesResponse {
  you: { adminId: string; role: AtlasRole; scope: string; sees: string };
  assignments: AtlasRoleAssignment[];
  /** The exact contents of the role dropdown. Never hardcode the four roles. */
  assignableRoles: AtlasRole[];
  roleMatrix: Record<string, AtlasRoleDomains>;
  /** false → hide the Scope column entirely; trips carry no city tag yet. */
  marketsEnabled: boolean;
}

export interface AtlasAssignRoleRequest {
  targetAdminId: string;
  role: AtlasRole;
  scope?: string;
}

export interface AtlasAssignRoleResponse {
  ok: boolean;
  assignment: AtlasRoleAssignment;
}

// -------------------------------------------------------------- alerts

export interface AtlasAlertHistoryEntry {
  status: string;
  actor?: string;
  note?: string;
  at: string;
}

export interface AtlasAlert {
  id: string;
  kind: 'finance' | 'fraud' | 'safety' | 'quality' | 'opportunity';
  category?: string | null;
  urgency: 'urgent' | 'daily';
  status: string;
  title: string;
  body: string;
  /** Shape varies by monitor — rating flags, chat safety and refund abuse all differ. */
  evidence: Record<string, unknown>[];
  entityId: string;
  recipients?: string[];
  pushed?: boolean;
  at: string;
  history: AtlasAlertHistoryEntry[];
  updatedAt?: string;
  lastActor?: string;
}

export interface AtlasAlertsResponse {
  alerts: AtlasAlert[];
  /** Build the filter chips from this — it is this caller's real access. */
  allowedKinds: string[];
  statuses: string[];
}

// ------------------------------------------------------------ advisory

export interface AtlasAdvisoryItem {
  id: string;
  kind: 'strength' | 'issue';
  category: string;
  title: string;
  detail: string;
  action: string;
  dollarsAtStake: number;
  confidence?: number;
  /** Present when Atlas believes its own number may be understated. Always show it. */
  uncertainty?: string;
}

export interface AtlasAdvisoryResponse {
  workingWell: AtlasAdvisoryItem[];
  needsLook: AtlasAdvisoryItem[];
  generatedFrom: number;
}

// --------------------------------------------------------------- error

/**
 * Carries the HTTP status and Atlas error code through to callers, which need to
 * branch on them: 403 hides a feature, 404 means "no longer available" rather than
 * "access denied", 429 means back off.
 */
export class AtlasError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    /** Seconds to wait, from the Retry-After header on a 429. */
    readonly retryAfter?: number
  ) {
    super(message);
    this.name = 'AtlasError';
  }
}

/**
 * Client for the Atlas service. Reuses the admin's existing VeloRide session token —
 * Atlas re-checks the role server-side, and the Anthropic key never reaches the browser.
 */
@Injectable({ providedIn: 'root' })
export class AtlasService {
  private http = inject(HttpClient);
  private auth = inject(AuthenticationService);

  /**
   * Ask a question. `history` holds the prior turns, so follow-ups keep context.
   * Answers routinely take 30–60s — the AI runs real queries — so never add a timeout.
   */
  ask(question: string, history: AtlasTurn[]): Observable<AtlasChatResponse> {
    return this.post<AtlasChatResponse>('/chat', { question, history, timezone: this.timezone() });
  }

  /** Fetch the proactive weakest-area brief. */
  getBriefing(scope?: string): Observable<AtlasBriefingResponse> {
    return this.post<AtlasBriefingResponse>('/briefing', { scope, timezone: this.timezone() });
  }

  /** Assignments visible to the caller. 403 means the caller has no Atlas role at all. */
  getRoles(): Observable<AtlasRolesResponse> {
    return this.get<AtlasRolesResponse>('/roles');
  }

  /**
   * Cached view of the caller's own Atlas role. Several surfaces ask "is this an Owner?"
   * — a route guard, a nav tab — and firing /roles for each would be wasteful.
   *
   * A 403 (no Atlas role at all) resolves to null rather than an error, because for
   * gating purposes "no role" and "not an owner" lead to the same place.
   */
  private ownRole$?: Observable<AtlasRolesResponse | null>;

  roleInfo(): Observable<AtlasRolesResponse | null> {
    if (!this.ownRole$) {
      this.ownRole$ = this.getRoles().pipe(
        catchError(() => of(null)),
        shareReplay({ bufferSize: 1, refCount: false })
      );
    }
    return this.ownRole$;
  }

  isOwner(): Observable<boolean> {
    return this.roleInfo().pipe(map((r) => r?.you?.role === 'owner'));
  }

  /** Drop the cache — call after anything that could change the caller's own role. */
  clearRoleCache(): void {
    this.ownRole$ = undefined;
  }

  /** Grant a role. The server re-checks delegation; a 403 here is final, never retry. */
  assignRole(body: AtlasAssignRoleRequest): Observable<AtlasAssignRoleResponse> {
    return this.post<AtlasAssignRoleResponse>('/roles', { scope: 'global', ...body });
  }

  getAlerts(params: { status?: string; limit?: number } = {}): Observable<AtlasAlertsResponse> {
    let httpParams = new HttpParams();
    if (params.status) httpParams = httpParams.set('status', params.status);
    if (params.limit) httpParams = httpParams.set('limit', String(params.limit));
    return this.get<AtlasAlertsResponse>('/alerts', httpParams);
  }

  transitionAlert(id: string, body: { status: string; note?: string }): Observable<{ ok: boolean }> {
    return this.post<{ ok: boolean }>(`/alerts/${id}/transition`, body);
  }

  getAdvisory(): Observable<AtlasAdvisoryResponse> {
    return this.get<AtlasAdvisoryResponse>('/advisory');
  }

  private timezone(): string {
    // Returns '' when Intl can't resolve a zone; never send an empty string.
    return getBrowserTimezone() || 'UTC';
  }

  /**
   * BasicAuthInterceptor only attaches the Bearer token to URLs under environment.apiUrl,
   * and the Atlas base sits outside that prefix — so set the header explicitly here.
   */
  private headers(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.auth.getToken() ?? ''}`
    });
  }

  private get<T>(path: string, params?: HttpParams): Observable<T> {
    return this.http
      .get<T>(`${environment.atlasUrl}${path}`, { headers: this.headers(), params })
      .pipe(catchError((err: HttpErrorResponse) => throwError(() => this.toAtlasError(err))));
  }

  private post<T>(path: string, body: Record<string, unknown>): Observable<T> {
    return this.http
      .post<T>(`${environment.atlasUrl}${path}`, body, { headers: this.headers() })
      .pipe(catchError((err: HttpErrorResponse) => throwError(() => this.toAtlasError(err))));
  }

  /**
   * Atlas answers with `{ error: "<code>", detail: "<human message>" }`. Prefer the
   * server's prose — it is written per endpoint — and fall back to mapping the bare
   * code, which would otherwise surface to an admin as the word "forbidden".
   */
  private toAtlasError(err: HttpErrorResponse): AtlasError {
    const body = err.error as Record<string, unknown> | null;
    const code = typeof body?.['error'] === 'string' ? (body['error'] as string) : '';
    const detail = typeof body?.['detail'] === 'string' ? (body['detail'] as string) : '';

    const retryHeader = err.headers?.get('Retry-After');
    const retryAfter = retryHeader ? Number(retryHeader) : undefined;

    return new AtlasError(detail || this.fallbackMessage(err, code, retryAfter), err.status, code, retryAfter);
  }

  private fallbackMessage(err: HttpErrorResponse, code: string, retryAfter?: number): string {
    switch (err.status) {
      case 401:
        return 'Your session has expired. Please sign in again.';
      case 403:
        return 'Your Atlas role does not include this.';
      case 404:
        return 'That is no longer available.';
      case 429:
        return retryAfter
          ? `Too many requests — try again in ${retryAfter}s.`
          : 'Too many requests — give it a moment and try again.';
      // status 0 means the request never landed — service down, or CORS rejected the origin.
      case 0:
        return 'Atlas is unreachable. Check the service and its CORS settings.';
      default:
        return code || `Atlas error ${err.status}`;
    }
  }
}
